/**
 * Lets any page open the Help Center without owning it. The panel lives in the
 * app shell (`shell/Layout.tsx`), which listens for this window event.
 *
 * `openHelpForPage` finds the help article filed under a page key (for example
 * `cockpit:it/service-desk`) and opens the panel on it. When the page has no
 * article yet, or the lookup fails, the panel opens on its home view instead so
 * the click always does something useful.
 *
 * A page key can have several articles. Pass `slugPrefix` to open the one whose
 * address starts with it; if none does, the panel opens on its home view rather
 * than on an unrelated article. `wide` opens the panel full width, for a long
 * article that is the point of the click.
 */

import type { HelpCenterView } from '@ampersandbrands/ui';
import { cockpitServiceDeskClient } from './serviceDeskClient';

export const OPEN_HELP_EVENT = 'cockpit:open-help';

export type OpenHelpDetail = { view?: HelpCenterView };

export function openHelp(view?: HelpCenterView): void {
  window.dispatchEvent(new CustomEvent<OpenHelpDetail>(OPEN_HELP_EVENT, { detail: { view } }));
}

export type OpenHelpForPageOptions = { slugPrefix?: string; wide?: boolean };

export async function openHelpForPage(pageKey: string, options: OpenHelpForPageOptions = {}): Promise<void> {
  try {
    const mapped = await cockpitServiceDeskClient.getCategoryForPage(pageKey);
    const categoryId = mapped?.subcategory?.id ?? mapped?.category?.id;
    if (categoryId) {
      const articles = await cockpitServiceDeskClient.searchArticles('', categoryId);
      const { slugPrefix } = options;
      const pick = slugPrefix === undefined ? articles[0] : articles.find((a) => a.slug.startsWith(slugPrefix));
      if (pick) {
        openHelp(options.wide ? { article: pick.id, wide: true } : { article: pick.id });
        return;
      }
    }
  } catch {
    // Fall through to the home view.
  }
  openHelp();
}
